/* =============================================================================
   BrandTheme — applies a brand's colours to the running site.
   -----------------------------------------------------------------------------
   The whole site is derived from FIVE colours. Set those five on :root and every
   other colour recomputes, because theme.css derives everything else with
   color-mix(). No re-render, no rebuild, one frame.

   That is what makes the planned S8MC brand-colour screen possible: it can call
   apply() on every keystroke of a colour picker and the preview follows live.

   ⚠ THE CONTRACT
   --------------
   These five names are the contract between S8MC and this site. If a sixth
   colour is ever needed, add it here AND derive it in theme.css. Never introduce
   a colour literal in a component — a brand cannot change it, and nobody finds
   out until that brand complains.
   ============================================================================= */

/** The five. Order matters only for how they are presented in S8MC. */
export const BRAND_TOKENS = [
  {
    key: 'accent',
    cssVar: '--brand-accent',
    label: 'Brand colour',
    help: 'Buttons, prices and the selected category. The one colour most people would call "your colour".',
    fallback: '#C2410C'
  },
  {
    key: 'accentInk',
    cssVar: '--brand-accent-ink',
    label: 'Text on the brand colour',
    help: 'Usually white. Set it to a dark colour if your brand colour is pale.',
    fallback: '#FFFFFF'
  },
  {
    key: 'ink',
    cssVar: '--brand-ink',
    label: 'Text colour',
    help: 'All body text. Everything grey on the page is mixed from this, so it sets the overall warmth.',
    fallback: '#1F1A17'
  },
  {
    key: 'surface',
    cssVar: '--brand-surface',
    label: 'Page background',
    help: 'The colour behind everything. Keep it very pale — product photography sits on it.',
    fallback: '#FAF7F4'
  },
  {
    key: 'highlight',
    cssVar: '--brand-highlight',
    label: 'Promotions',
    help: 'Deal banners and anything you want to stand apart from the brand colour.',
    fallback: '#7C2D12'
  }
];

/* -----------------------------------------------------------------------------
   The type pairings.
   -----------------------------------------------------------------------------
   ⚠ SHARED CONTRACT with the FontPairings list in
   OrderAheadUI/Settings/WebsiteConfigService.ashx.cs. The two must not drift:
   that list is what a brand picks from and what the server will accept on save,
   this one is what the site is willing to load. A pairing in one and not the
   other is a choice that saves and then does nothing, or a stored value the site
   silently ignores.

   This list is an ALLOWLIST, not a convenience. FontSans arrives from the API
   and ends up inside a font-family declaration and a stylesheet URL, so it is
   checked against known names rather than trusted — the server validates on
   save, but nothing guarantees the row was written by this version of that
   screen.
   --------------------------------------------------------------------------- */
export const FONT_PAIRINGS = [
  { sans: 'Figtree',       display: 'Fraunces' },          // the site's own default
  { sans: 'Inter',         display: 'Inter' },
  { sans: 'Poppins',       display: 'Poppins' },
  { sans: 'Lato',          display: 'Playfair Display' },
  { sans: 'Source Sans 3', display: 'Bitter' },
  { sans: 'Work Sans',     display: 'Oswald' },
  { sans: 'Nunito Sans',   display: 'Fraunces' }
];

/* The fallbacks each face falls back TO, so a family that has not downloaded yet
   — or cannot — lands on something of the same shape rather than on Times. */
const SANS_FALLBACK = "system-ui, -apple-system, 'Segoe UI', sans-serif";
const DISPLAY_FALLBACK = "Georgia, 'Times New Roman', serif";

/* Which families are serif, so a display face gets a serif fallback and a sans
   one does not. Only matters for the moment before the webfont arrives, which is
   precisely when a customer forms an impression of the page. */
const SERIF_FAMILIES = ['Fraunces', 'Playfair Display', 'Bitter'];

/* One <link> per family, not one for both. Two reasons, and the first is the
   one that matters: Google Fonts css2 answers 400 — not a partial stylesheet —
   for any weight or axis a family does not offer, so a single combined request
   that gets one family's weights wrong loads NEITHER face. Split, a mistake
   costs one of the two. Second, a pairing that uses the same family twice then
   needs only one request. */
const FONT_LINK_IDS = ['woBrandFontSans', 'woBrandFontDisplay'];

const HEX = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;

/** Only apply something that is unmistakably a colour. A bad value from an API
 *  should leave the default in place, not blank the page. */
function usable(value) {
  return typeof value === 'string' && HEX.test(value.trim());
}

/**
 * Apply a brand's colours.
 *
 * @param {object} brand  any subset of { accent, accentInk, ink, surface, highlight }
 * @param {HTMLElement} [target]  defaults to :root. Pass a container to scope a
 *                                preview, so S8MC can show a themed sample
 *                                without recolouring its own chrome.
 */
export function apply(brand, target) {
  const el = target || document.documentElement;
  if (!brand) { return; }

  BRAND_TOKENS.forEach(function (token) {
    const value = brand[token.key];
    if (usable(value)) {
      el.style.setProperty(token.cssVar, value.trim());
    }
  });
}

/**
 * Apply a brand's type.
 *
 * Sets --font-sans and --font-display, which is all of it: theme.css routes
 * every font-family through those two, so nothing re-renders and no component
 * knows a font changed.
 *
 * The chosen pairing's stylesheet is loaded from Google Fonts in ONE <link>,
 * replaced on each call rather than appended — otherwise picking three pairings
 * in a row in the S8MC preview leaves three stylesheets fighting over the same
 * families.
 *
 * @param {object} fonts  { sans, display } — must be a pairing from FONT_PAIRINGS
 */
export function applyFonts(fonts, target) {
  const el = target || document.documentElement;
  if (!fonts) { return; }

  const sans = String(fonts.sans || '').trim();
  const display = String(fonts.display || '').trim();

  // Checked as a PAIR against the allowlist. Half of one pairing with half of
  // another is a combination nobody looked at, and accepting the two names
  // independently is what would let an unknown family through.
  const allowed = FONT_PAIRINGS.some(function (p) {
    return p.sans === sans && p.display === display;
  });
  if (!allowed) {
    // Not an error worth showing a customer. Leave the site's own faces in
    // place — the alternative is an unstyled page because a config row is stale.
    return;
  }

  el.style.setProperty('--font-sans', "'" + sans + "', " + SANS_FALLBACK);
  el.style.setProperty('--font-display', "'" + display + "', " + fallbackFor(display));

  // Figtree and Fraunces are already in theme.css's own @import, so the default
  // pairing needs no second request.
  if (sans === 'Figtree' && display === 'Fraunces') {
    removeFontLink();
    return;
  }
  loadFontStylesheet(sans, display);
}

function fallbackFor(family) {
  return SERIF_FAMILIES.indexOf(family) === -1 ? SANS_FALLBACK : DISPLAY_FALLBACK;
}

function loadFontStylesheet(sans, display) {
  setFontLink(FONT_LINK_IDS[0], sans);
  // The same family twice is a legitimate pairing — Inter/Inter, Poppins/Poppins
  // — and must not be requested twice.
  setFontLink(FONT_LINK_IDS[1], sans === display ? null : display);
}

function setFontLink(id, family) {
  let link = document.getElementById(id);

  if (!family) {
    if (link && link.parentNode) { link.parentNode.removeChild(link); }
    return;
  }

  // display=swap, deliberately: the page renders immediately in the fallback and
  // swaps when the face arrives. The alternative is invisible text on a menu for
  // however long the font takes on a phone on mobile data.
  const href = 'https://fonts.googleapis.com/css2?family='
    + encodeURIComponent(family).replace(/%20/g, '+') + ':wght@' + WEIGHTS
    + '&display=swap';

  if (link && link.getAttribute('href') === href) { return; }
  if (!link) {
    link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    document.head.appendChild(link);
  }
  link.setAttribute('href', href);
}

/* ⚠ 400 and 700 ONLY, and that is a deliberate retreat from what the stylesheet
   asks for.

   theme.css uses 500, 600 and 800 as well, and italic — its own @import requests
   all of them for Figtree. But those are not universal: Lato is a static family
   offering 100/300/400/700/900 and no 500, 600 or 800, and Oswald has no italic
   at any weight. Google Fonts css2 answers 400 to a request for a weight a
   family does not have, so asking for the full set would make Lato and Oswald
   load NOTHING — silently, with the page falling back to system-ui and looking
   merely unstyled rather than broken.

   400 and 700 are offered by all eleven families on the list. The weights in
   between fall to the browser's nearest available face, which is ordinary
   degradation and visible to nobody but a designer.

   ⚠ The proper fix is a per-family axis list carried on each FONT_PAIRINGS row,
   VERIFIED against Google Fonts rather than reasoned about — a wrong entry fails
   silently and in exactly the way this comment describes. That belongs with the
   design pass on Q8, which is also where the pairings themselves get looked at
   on a real page for the first time. Do not widen this constant without it. */
const WEIGHTS = '400;700';

function removeFontLink() {
  FONT_LINK_IDS.forEach(function (id) {
    const link = document.getElementById(id);
    if (link && link.parentNode) { link.parentNode.removeChild(link); }
  });
}

/** Put everything back to the Shift8 defaults. */
export function reset(target) {
  const el = target || document.documentElement;
  BRAND_TOKENS.forEach(function (token) { el.style.removeProperty(token.cssVar); });
  el.style.removeProperty('--font-sans');
  el.style.removeProperty('--font-display');
  removeFontLink();
}

/** What is actually in force right now — for a preview panel, or for debugging
 *  a brand that says their colour "did not apply". */
export function current(target) {
  const el = target || document.documentElement;
  const computed = getComputedStyle(el);
  const out = {};
  BRAND_TOKENS.forEach(function (token) {
    out[token.key] = computed.getPropertyValue(token.cssVar).trim() || token.fallback;
  });
  return out;
}

/* -----------------------------------------------------------------------------
   Adapter for what the API returns TODAY.
   -----------------------------------------------------------------------------
   The existing endpoint returns only { backgroundColour, textColour }, and
   App.js injects a <style> block using them for three selectors. Until the S8MC
   screen exists, map the old pair onto the new five so nothing regresses.

   ⚠ Read the old names carefully. In the current API "backgroundColour" is the
   colour used as the FILL of the selected category pill — it is the brand's
   accent, not the page background — and "textColour" is what sits on top of it.
   Mapping backgroundColour onto --brand-surface would turn the whole page that
   colour, which is exactly the kind of mistake this comment exists to prevent.
   --------------------------------------------------------------------------- */
export function fromLegacyResponse(legacy) {
  if (!legacy) { return {}; }
  return {
    accent:    legacy.backgroundColour,   // the pill fill = the brand colour
    accentInk: legacy.textColour,         // text on it
    highlight: legacy.backgroundColour     // until brands can set one of their own
  };
}

/* -----------------------------------------------------------------------------
   Adapter for the Website Config screen's own response.
   -----------------------------------------------------------------------------
   Shape: BrandDetails → websiteConfig, as BrandDetailsController composes it.

   ⚠ The server sends the CSS custom property names, not the column names —
   brandAccent, not ColourAccent — so renaming a token breaks loudly on both
   sides instead of silently on one. Note the lower-cased first letter: Web API
   camel-cases what it serialises.

   A brand that has never opened the screen sends null, and every field inside it
   may be null on its own. applyFromConfig does nothing with those, which leaves
   the site's own defaults in place — the correct outcome, not a fallback.
   --------------------------------------------------------------------------- */
export function fromConfig(websiteConfig) {
  if (!websiteConfig || !websiteConfig.colours) { return {}; }
  const c = websiteConfig.colours;
  return {
    accent:    c.brandAccent,
    accentInk: c.brandAccentInk,
    ink:       c.brandInk,
    surface:   c.brandSurface,
    highlight: c.brandHighlight
  };
}

/** Colours and type together, from one API response. */
export function applyFromConfig(websiteConfig, target) {
  if (!websiteConfig) { return; }
  apply(fromConfig(websiteConfig), target);
  applyFonts({ sans: websiteConfig.fontSans, display: websiteConfig.fontDisplay }, target);
}

const BrandTheme = {
  BRAND_TOKENS, FONT_PAIRINGS,
  apply, applyFonts, reset, current,
  fromLegacyResponse, fromConfig, applyFromConfig
};
export default BrandTheme;
